import { useState } from 'react';
import { View, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Feather } from '@expo/vector-icons';
import { spacing } from '../../theme';
import { useColors, ThemeColors } from '../../hooks/useColors';

interface ChatInputProps {
  onSend: (text: string) => void;
}

export function ChatInput({ onSend }: ChatInputProps) {
  const colors = useColors();
  const insets = useSafeAreaInsets();
  const styles = createStyles(colors, insets);
  const [message, setMessage] = useState('');

  const handleSend = () => {
    if (message.trim()) {
      onSend(message.trim());
      setMessage('');
    }
  };

  return (
    <View style={styles.container}>
      <TextInput
        style={styles.input}
        value={message}
        onChangeText={setMessage}
        placeholder="Type a message..."
        placeholderTextColor={colors.textLight}
        multiline={true}
        numberOfLines={1}
        maxLength={1000}
        textAlignVertical="center"
        blurOnSubmit={false}
        returnKeyType="send"
        onSubmitEditing={handleSend}
        enablesReturnKeyAutomatically={true}
      />
      <TouchableOpacity 
        style={[
          styles.sendButton,
          !message.trim() && styles.sendButtonDisabled
        ]}
        onPress={handleSend}
        disabled={!message.trim()}
      >
        <Feather 
          name="send" 
          size={20} 
          color={message.trim() ? colors.surface : colors.textLight} 
        />
      </TouchableOpacity>
    </View>
  );
}

const createStyles = (colors: ThemeColors, insets: any) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: Math.max(insets.bottom + spacing.sm, spacing.lg),
    backgroundColor: colors.background,
    gap: spacing.sm,
    // Add shadow to make it feel like a dock
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 10,
  },
  input: {
    flex: 1,
    minHeight: 48,
    maxHeight: 120,
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: colors.surface,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: colors.border,
    fontSize: 15,
    color: colors.text,
    lineHeight: 20,
    textAlignVertical: 'top',
  },
  sendButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
    marginBottom: 0,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sendButtonDisabled: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderWidth: 1,
    shadowOpacity: 0,
    elevation: 0,
  },
}); 